import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  Checkbox,
  Grid,
  TextField,
  Container,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl, FormHelperText,
  Chip,
  OutlinedInput,
  Select,
  MenuItem,
  InputLabel,
  ListItemText,
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { Card, CardHeader } from '@ui5/webcomponents-react';

import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import httpRequest from "../../httpRequest";

const EmployeesCard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [selectedFunctionIds, setSelectedFunctionIds] = useState([]);
  const [employeeCode, setEmployeeCode] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [positionId, setPositionId] = useState('');
  const [baseCompanyId, setBaseCompanyId] = useState('');
  const [baseFunctionId, setBaseFunctionId] = useState('');
  const [address, setAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [designation, setDesignation] = useState('');
  const [erpCode, setErpCode] = useState('');
  const [costCenter, setCostCenter] = useState('');


  useEffect(() => {
    fetchEmployees();
    fetchCompanies();
    fetchFunctions();
    fetchPositions();
  }, []);

  useEffect(() => {
    if (selectedCompanyIds.length > 0) {
      setBaseCompanyId(selectedCompanyIds[0]);
    }
  }, [selectedCompanyIds]);
  
  useEffect(() => {
    if (selectedFunctionIds.length > 0) {
      setBaseFunctionId(selectedFunctionIds[0]);
    }
  }, [selectedFunctionIds]);
  

  const fetchEmployees = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-employees`);
      setEmployees(response.data.employees);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-companies`);
      setCompanies(response.data.companies);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchFunctions = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/functions`);
      setFunctions(response.data.functions);
    } catch (error) {
      console.error("Error fetching functions:", error);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-positions`);
      setPositions(response.data.positions);
    } catch (error) {
      console.error("Error fetching positions:", error);
    }
  };

  const handleDialogOpen = (employee = {}) => {
    if (employee.id) {
      setIsEditing(true);
      setEmployeeCode(employee.employee_code);
      setEmployeeName(employee.employee_name);
      setPositionId(employee.position_id);
      setAddress(employee.address || '');
      setPhoneNumber(employee.phone_number || '');
      setEmail(employee.email);
      setLocation(employee.location || '');
      setDesignation(employee.designation || '');
      setErpCode(employee.erp_code || '');
	  setCostCenter(employee.cost_center || '');
	  setBaseCompanyId(employee.company_id);
	  setBaseFunctionId(employee.function_id);
      setSelectedCompanyIds(employee.companies || []);
      setSelectedFunctionIds(employee.functions || []);
      setSelectedEmployeeId(employee.id);
    } else {
      setIsEditing(false);
      setEmployeeCode('');
      setEmployeeName('');
      setPositionId('');
      setAddress('');
      setPhoneNumber('');
      setEmail('');
      setLocation('');
      setDesignation('');
      setErpCode('');
	  setCostCenter('');
	  setBaseCompanyId('');
	  setBaseFunctionId('');
      setSelectedCompanyIds([]);
      setSelectedFunctionIds([]);
    }
    setDialogOpen(true);
  };

  const handleDialogClose = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      // Prevent the dialog from closing when clicking on the backdrop
      return;
    }
    setDialogOpen(false);
    setSelectedEmployeeId(null);
  };

  const handleAddOrUpdateEmployee = async () => {
    setFormSubmitted(true);

    if (!employeeCode || !employeeName || !positionId || !email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      return;
    }
    const employeeData = {
      employee_code: employeeCode,
      employee_name: employeeName,
      position_id: positionId,
      address: address,
      phone_number: phoneNumber,
      email: email,
      location: location,
      designation: designation,
      erp_code: erpCode,
	  cost_center: costCenter,
	  company_id: baseCompanyId,
	  function_id: baseFunctionId,
      company_ids: selectedCompanyIds,
      function_ids: selectedFunctionIds,
    };

    try {
      if (isEditing) {
        await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/update-employee/${selectedEmployeeId}`, employeeData);
      } else {
        await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/add-employee`, employeeData);
      }
      fetchEmployees();
      setFormSubmitted(false);
      handleDialogClose();
      enqueueSnackbar(`Employee ${isEditing ? 'updated' : 'added'} successfully`, { variant: 'success' });
    } catch (error) {
      console.error("Error adding/updating employee:", error);
    }
  };

  const handleDeleteEmployees = async () => {
    try {
      await Promise.all(selectedEmployees.map(id => httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-employee/${id}`)));
      fetchEmployees();
      handleCloseConfirmDialog();
      enqueueSnackbar('Selected employee(s) deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error("Error deleting employees:", error);
      enqueueSnackbar('Error deleting employees', { variant: 'error' });
    }
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]
    );
  };

  const columns = [
    {
      field: 'employee_code',
      headerName: 'Employee Code',
      width: 150,
    },
    {
      field: 'employee_name',
      headerName: 'Employee Name',
      width: 200,
    },
    {
      field: 'position_name',
      headerName: 'Position',
      width: 150,
      valueGetter: (params) => positions.find(pos => pos.id === params.row.position_id)?.position_name || '',
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 250,
    },
    {
      field: 'phone_number',
      headerName: 'Phone Number',
      width: 150,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 150,
    },
    {
      field: 'designation',
      headerName: 'Designation',
      width: 150,
    },
    {
      field: 'erp_code',
      headerName: 'ERP Code',
      width: 150,
    },
	{
      field: 'cost_center',
      headerName: 'Cost Center',
      width: 150,
    },
	{
      field: 'company_name',
      headerName: 'Base Company',
      width: 150,
      valueGetter: (params) => companies.find(company => company.id === params.row.company_id)?.company_name || '',
    },
	{
      field: 'function_name',
      headerName: 'Base Function',
      width: 150,
      valueGetter: (params) => functions.find(func => func.id === params.row.function_id)?.function_name || '',
    },
    {
      field: 'companies',
      headerName: 'Companies',
      width: 200,
      valueGetter: (params) => {
        return params.row.companies
          .map((id) => companies.find(company => company.id === id)?.company_name)
          .filter(Boolean)
          .join(', ');
      },
    },
    {
      field: 'functions',
      headerName: 'Functions',
      width: 200,
      valueGetter: (params) => {
        return params.row.functions
          .map((id) => functions.find(func => func.id === id)?.function_name)
          .filter(Boolean)
          .join(', ');
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <ButtonOutlined onClick={() => handleDialogOpen(params.row)}>
          Edit
        </ButtonOutlined>
      ),
    },
  ];

  const filteredEmployees = employees.filter((emp) => {
    const query = searchQuery.toLowerCase();
    return (
      emp.employee_code.toLowerCase().includes(query) ||
      emp.employee_name.toLowerCase().includes(query) ||
      (positions.find((pos) => pos.id === emp.position_id)?.position_name || '').toLowerCase().includes(query) ||
      (emp.address || '').toLowerCase().includes(query) ||
      (emp.phone_number || '').includes(query) ||
      (emp.email || '').toLowerCase().includes(query) ||
      (emp.location || '').toLowerCase().includes(query) ||
      (emp.designation || '').toLowerCase().includes(query) ||
      (emp.erp_code || '').toLowerCase().includes(query) ||
	  (emp.cost_center || '').toLowerCase().includes(query) ||
	  (companies.find((company) => company.id === emp.company_id)?.company_name || '').toLowerCase().includes(query) ||
	  (functions.find((func) => func.id === emp.function_id)?.function_name || '').toLowerCase().includes(query) ||
      emp.companies.some((id) => companies.find(company => company.id === id)?.company_name.toLowerCase().includes(query)) ||
      emp.functions.some((id) => functions.find(func => func.id === id)?.function_name.toLowerCase().includes(query))
    );
  });

  return (
  <>
<Card header={<CardHeader subtitleText="Create and manage employees" titleText="Employees" />} sx={{ width: "100%", overflow: "hidden" }}>

        <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

    <input
	  type="text"
      placeholder="Search Employees..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
	              style={{ flex: "1", padding: "10px", marginRight: "20px" }}

    />

  <ButtonOutlined
  onClick={handleOpenConfirmDialog}
  {...(selectedEmployees.length === 0 ? {disabled: true} : {})}  style={{ marginRight: 8 }}><DeleteIcon />
      Delete Selected
    </ButtonOutlined>
    <ButtonContained onClick={handleDialogOpen}>
	<AddIcon />
      Add Employee
    </ButtonContained>
 
</div>


            <DataGrid
              rows={filteredEmployees}
              columns={columns}
				 					sx={{ fontFamily: "SapFiori72, sans-serif" }}

              checkboxSelection
              onSelectionModelChange={(ids) => setSelectedEmployees(ids)}
              getRowId={(row) => row.id}
			    autoHeight
            />

        </Card>
      <Dialog open={dialogOpen} onClose={handleDialogClose} slotProps={{
        backdrop: {
          onClick: (e) => {
            e.stopPropagation(); // Prevent the click from propagating
          },
        },
      }}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee Code"
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value)}
                required
                error={formSubmitted && !employeeCode}
    helperText={formSubmitted && !employeeCode ? 'Employee Code is required' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee Name"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                required
                error={formSubmitted && !employeeName}
    helperText={formSubmitted && !employeeName ? 'Employee Name is required' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={formSubmitted && !positionId}>
            <InputLabel>Position</InputLabel>
                <Select
                  value={positionId}
                  onChange={(e) => setPositionId(e.target.value)}
                >
                  {positions.map((position) => (
                    <MenuItem key={position.id} value={position.id}>
                      {position.position_name}
                    </MenuItem>
                  ))}
                </Select>
                {formSubmitted && !positionId && <FormHelperText>Position is required</FormHelperText>}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
            <TextField
                fullWidth
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                error={formSubmitted && (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email))}
    helperText={formSubmitted && (!email ? 'Email is required' : !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email) ? 'Invalid email address' : '')}
    pattern="[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$"
              />
              
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
            <TextField
                fullWidth
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Designation"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ERP Code"
                value={erpCode}
                onChange={(e) => setErpCode(e.target.value)}
              />
            </Grid>
			<Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost Center"
                value={costCenter}
                onChange={(e) => setCostCenter(e.target.value)}
              />
            </Grid>
			      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Companies</InputLabel>
          <Select
            multiple
            value={selectedCompanyIds}
            onChange={(e) => setSelectedCompanyIds(e.target.value)}
            input={<OutlinedInput label="Companies" />}
            renderValue={(selected) =>
              selected
                .map((id) => companies.find(company => company.id === id)?.company_name)
                .filter(Boolean)
                .join(", ")
            }
          >
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                <Checkbox checked={selectedCompanyIds.includes(company.id)} />
                <ListItemText primary={company.company_name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Functions</InputLabel>
          <Select
            multiple
            value={selectedFunctionIds}
            onChange={(e) => setSelectedFunctionIds(e.target.value)}
            input={<OutlinedInput label="Functions" />}
            renderValue={(selected) =>
              selected
                .map((id) => functions.find(func => func.id === id)?.function_name)
                .filter(Boolean)
                .join(", ")
            }
          >
            {functions.map((func) => (
              <MenuItem key={func.id} value={func.id}>
                <Checkbox checked={selectedFunctionIds.includes(func.id)} />
                <ListItemText primary={func.function_name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {selectedCompanyIds.length > 0 && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Base Company</InputLabel>
            <Select
              value={baseCompanyId}
              onChange={(e) => setBaseCompanyId(e.target.value)}
              disabled={selectedCompanyIds.length === 0}
            >
              {selectedCompanyIds.map((id) => {
                const company = companies.find(company => company.id === id);
                return (
                  <MenuItem key={company?.id} value={company?.id}>
                    {company?.company_name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}

      {selectedFunctionIds.length > 0 && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Base Function</InputLabel>
            <Select
              value={baseFunctionId}
              onChange={(e) => setBaseFunctionId(e.target.value)}
              disabled={selectedFunctionIds.length === 0}
            >
              {selectedFunctionIds.map((id) => {
                const func = functions.find(func => func.id === id);
                return (
                  <MenuItem key={func?.id} value={func?.id}>
                    {func?.function_name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}
    </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonOutlined onClick={handleDialogClose}>Cancel</ButtonOutlined>
          <ButtonContained onClick={handleAddOrUpdateEmployee}>
            {isEditing ? 'Update' : 'Add'}
          </ButtonContained>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Deleting Employees */}
      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogContent>
          Are you sure you want to delete the selected employee(s)?
        </DialogContent>
        <DialogActions>
          <ButtonOutlined onClick={handleCloseConfirmDialog}>Cancel</ButtonOutlined>
          <ButtonContained color="secondary" onClick={handleDeleteEmployees}>
            Delete
          </ButtonContained>
        </DialogActions>
      </Dialog>
	  </>
  );
};

export default EmployeesCard;
